import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import ElitePage from './ElitePage';
import {
  useResolveAffiliateSlugQuery,
  useLogAffiliateClickMutation,
} from 'store/services/affiliates/affiliatesApi';
import { setTmRefCookie, getOrCreateAnonymousId } from 'utils/referralCookies';

/**
 * /elite/:slug — affiliate referral landing (SPA-navigation path).
 *
 * In production CloudFront routes /elite/* to the backend origin, which serves
 * the SPA shell with the attribution cookie + dataLayer push server-side. This
 * wrapper is the equivalent for client-side navigations and the fallback when
 * the request didn't pass through that route: resolve the slug against active
 * affiliates, and when valid set tm_ref (30-day sliding, last-touch), push the
 * GA4 UTM event, and log the click (backend dedupes per anonymous id / 24h).
 *
 * Reserved words and unknown slugs render the plain Elite page with no
 * attribution (the backend logs invalid_affiliate_slug on resolve).
 */

export const RESERVED_ELITE_SLUGS = [
  'features',
  'faq',
  'pricing',
  'compare',
  'benefits',
  'terms',
  'cancel',
  'manage',
];

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const EliteReferralRoute: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const isReserved = RESERVED_ELITE_SLUGS.includes((slug || '').toLowerCase());

  const { data: resolved } = useResolveAffiliateSlugQuery(slug || '', {
    skip: !slug || isReserved,
  });
  const [logClick] = useLogAffiliateClickMutation();

  // One attribution per slug per mount (resolve refetches must not re-log).
  const attributedSlugRef = useRef<string | null>(null);

  useEffect(() => {
    if (!slug || isReserved) return;
    if (!resolved?.valid || !resolved.username) return;
    if (attributedSlugRef.current === resolved.username) return;
    attributedSlugRef.current = resolved.username;

    // Canonical-cased username from the resolver, matching the server-side route.
    setTmRefCookie(resolved.username);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'affiliate_ref',
      utm_source: 'affiliate',
      utm_medium: 'referral',
      utm_campaign: resolved.username,
    });

    logClick({
      slug: resolved.username,
      landingUrl: window.location.href,
      anonymousId: getOrCreateAnonymousId(),
    });
  }, [slug, isReserved, resolved, logClick]);

  return <ElitePage />;
};

export default EliteReferralRoute;
